import { StarterKit } from "@tiptap/starter-kit";
import { CommonModule } from "@angular/common";
import {
  ApplicationRef,
  Component,
  EnvironmentInjector,
  inject,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  signal,
  AfterViewInit,
  ChangeDetectionStrategy
} from "@angular/core";
import { TiptapEditorDirective } from "ngx-tiptap";

// Text formatting
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Underline } from "@tiptap/extension-underline";
import { TextAlign } from "@tiptap/extension-text-align";
import { FontFamily } from "@tiptap/extension-font-family";

// Lists
import { OrderedList } from "@tiptap/extension-ordered-list";
import { BulletList } from "@tiptap/extension-bullet-list";
import { TaskList } from "@tiptap/extension-task-list";
import { TaskItem } from "@tiptap/extension-task-item";

// Media & Links
import { Link } from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";

// Tables
import { GpdButtonComponent } from "src/app/components/gpd-button/gpd-button.component";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";

//Docuemnts
import { Dropcursor } from "@tiptap/extension-dropcursor";
import { ResizableImage } from "./resizable-image";

import { v4 as uuidv4 } from "uuid";
import {
  DxColorBoxModule,
  DxDropDownBoxModule,
  DxListModule,
  DxSelectBoxModule,
  DxTextBoxModule,
  DxCheckBoxModule,
  DxTagBoxModule,
} from "devextreme-angular";
import { FontSize } from "./font-size-extension";
import { SvgIconComponent } from "src/app/components/svg-icon/svg-icon.component";
import { Editor } from "@tiptap/core";
import { ProjectHubSpark, tagColor } from "../../project-hub.types";
import { AppGlobals } from "src/app/app.globals";
import { ProjectHubService } from "src/app/services/project-hub.service";
import { ActivatedRoute } from "@angular/router";
import { DocumentAttachment } from "./document-attachment";
import Table from "@tiptap/extension-table";
import { AppConfig } from "src/app/app.config";
import { ConfigService } from "src/app/services/config.service";
import { Subscription } from "rxjs";
import { createUserMention, MentionableUser } from "./mention-extension";

import { SearchableDropdownDirective } from '../../../../../../../directives/searchable-dropdown.directive';
@Component({
    selector: "app-spark-editor-tiptap",
    imports: [
        CommonModule,
        TiptapEditorDirective,
        GpdButtonComponent,
        DxDropDownBoxModule,
        DxListModule,
        DxSelectBoxModule,
        SvgIconComponent,
        DxColorBoxModule,
        DxTextBoxModule,
        DxCheckBoxModule,
        DxTagBoxModule,
        SearchableDropdownDirective,
    ],
    templateUrl: "./spark-editor-tiptap.component.html",
    styleUrl: "./spark-editor-tiptap.component.css",
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class SparkEditorTiptapComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  isEditable: boolean = true;

  imageFiles: { id: string; file: File }[] = [];

  @Input() sparkTags: { id: number; name: string; color?: tagColor }[] = [];
  @Input() spark: ProjectHubSpark | null = null;

  @Output() onSparkSaved = new EventEmitter<void>();
  @Output() onSparkCancelled = new EventEmitter<void>();

  boardId: number;

  toolbarIconStrokeWidth: number = 1.5;

  isLoading = signal<boolean>(false);

  showTablePicker: boolean = false;
  hoveredRows: number = 0;
  hoveredCols: number = 0;
  maxRows: number = 8;
  maxCols: number = 8;
  rows = Array(this.maxRows);
  cols = Array(this.maxCols);

  popupPosition: { top: number; left: number } = { top: 0, left: 0 };

  private readonly imageCache = new Map<string, string>();
  private readonly loadingImageIds = new Set<string>();

  activeTextModifications: {
    bold: boolean;
    italic: boolean;
    underline: boolean;
  } = {
    bold: false,
    italic: false,
    underline: false,
  };

  fontSizes: string[] = [
    "8px",
    "10px",
    "12px",
    "14px",
    "16px",
    "18px",
    "20px",
    "24px",
    "28px",
    "32px",
    "36px",
    "40px",
    "48px",
    "56px",
    "64px",
    "72px",
  ];

  defaultColor: string = "#000000";

  currentFontSize: string = "14px"; //default size
  currentFontColor: string = this.defaultColor; //default black
  capturedFiles: any;

  private isUpdatingFromSelection: boolean = false;

  private mentionableUsers: MentionableUser[] = [];
  private userListSubscription: Subscription;
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);

  constructor(
    private projectHubService: ProjectHubService,
    private route: ActivatedRoute,
    private configService: ConfigService,
  ) {
    this.boardId = parseInt(this.route.snapshot.paramMap.get("boardId"));
  }

  onColorChanged(event: any): void {
    if (event.event == undefined) return;
    this.currentFontColor = event.value;
    this.execute("setFontColor", event.value);
  }

  ngAfterViewInit(): void {
    if (this.spark?.content?.text) {
      this.loadExistingImages();
    }
  }

  private loadExistingImages(): void {
    if (!this.spark?.content?.text) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(this.spark.content.text, "text/html");
    const images = doc.querySelectorAll("img[data-file-id]");

    images.forEach((img) => {
      const fileId = img.getAttribute("data-file-id");
      if (
        fileId &&
        !this.imageCache.has(fileId) &&
        !this.loadingImageIds.has(fileId) &&
        !fileId.startsWith("new_")
      ) {
        this.loadingImageIds.add(fileId);
        this.loadAuthenticatedImage(fileId);
      }
    });
  }

  // Load authenticated image for display
  private async loadAuthenticatedImage(fileId: string): Promise<void> {
    try {
      const blob = await this.projectHubService.GetSparkAttachment(fileId);
      const imageUrl = URL.createObjectURL(blob);
      this.imageCache.set(fileId, imageUrl);
      this.loadingImageIds.delete(fileId);

      setTimeout(() => {
        const imgElements = document.querySelectorAll(
          `img[data-file-id='${fileId}']`,
        );
        imgElements.forEach((imgElement: HTMLImageElement) => {
          imgElement.src = imageUrl;
          imgElement.removeAttribute("data-loading");
          const pos = this.editor.state.doc.nodeAt(0);
        });

        this.editor.state.doc.descendants((node, pos) => {
          if (
            node.type.name === "resizableImage" &&
            node.attrs["data-file-id"] === fileId
          ) {
            this.editor
              .chain()
              .focus()
              .command(({ tr }) => {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  src: imageUrl,
                });
                return true;
              })
              .run();
          }
        });

        console.log(`Loaded and bound image for fileId: ${fileId}`);
      }, 100);
    } catch {
      this.loadingImageIds.delete(fileId);
    }
  }

  toggleTablePicker(button: HTMLElement) {
    if (this.showTablePicker) {
      this.showTablePicker = false;
      return;
    }

    const toolbar = button.closest(".toolbar");
    if (toolbar) {
      const toolbarRect = toolbar.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      this.popupPosition = {
        top: buttonRect.bottom - toolbarRect.top + 6,
        left: buttonRect.left - toolbarRect.left,
      };
    } else {
      // fallback to absolute positioning
      const rect = button.getBoundingClientRect();
      this.popupPosition = {
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
      };
    }
    this.showTablePicker = true;
  }

  hoverTableSize(rows: number, cols: number) {
    this.hoveredRows = rows;
    this.hoveredCols = cols;
  }

  isSelected(row: number, col: number): boolean {
    return row <= this.hoveredRows && col <= this.hoveredCols;
  }

  insertTable(rows: number, cols: number) {
    this.editor
      ?.chain()
      .focus()
      .insertTable({ rows, cols, withHeaderRow: true })
      .run();
    this.showTablePicker = false;
  }

  @Input() content = "";
  @Output() contentChange = new EventEmitter<string>();
  editor!: Editor;

  ngOnInit(): void {
    this.userListSubscription = this.configService.userList$.subscribe(
      (users) => (this.mentionableUsers = users ?? []),
    );
    this.configService.loadUserList();

    this.editor = new Editor({
      parseOptions: {
        preserveWhitespace: "full",
      },
      extensions: [
        StarterKit.configure({
          bulletList: false,
          orderedList: false,
        }),
        TextStyle.extend({
          parseHTML() {
            return [
              {
                tag: "span",
                getAttrs: (element) => {
                  const hasStyles = (element as HTMLElement).hasAttribute(
                    "style",
                  );
                  if (!hasStyles) {
                    return false;
                  }
                  return {};
                },
              },
            ];
          },
        }),
        Color.configure({
          types: ["textStyle"],
        }),
        Underline,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        FontSize,
        FontFamily.configure({
          types: ["textStyle"],
        }),
        OrderedList,
        BulletList,
        TaskList,
        TaskItem,
        Table,
        TableRow,
        TableCell,
        TableHeader,
        Link.configure({
          openOnClick: true,
          autolink: true,
          linkOnPaste: true,
        }),
        Image,
        ResizableImage,
        DocumentAttachment,
        Dropcursor,
        createUserMention(() => this.mentionableUsers, this.appRef, this.injector),
      ],
      content: this.spark?.content?.text || "<p>  </p>",
      onUpdate: ({ editor }) => {
        this.spark.content.text = editor.getHTML();
        console.log("Editor content updated:", editor.getHTML());
      },
      editorProps: {
        handleDrop: (view, event, _slice, moved) =>
          this.handleFileDrop(view, event),
      },
    });

    setTimeout(() => {
      const editorElement = document.querySelector(".tiptap, .ProseMirror");
      if (editorElement) {
        editorElement.addEventListener("click", (event: MouseEvent) => {
          const target = event.target as HTMLElement;
          if (target.tagName === "A" && target.getAttribute("href")) {
            const href = target.getAttribute("href");
            if (
              href &&
              event.button === 0 &&
              !event.ctrlKey &&
              !event.metaKey
            ) {
              window.open(href, "_blank");
              event.preventDefault();
              event.stopPropagation();
            }
          }
        });
      }
    }, 500);

    this.editor.on("selectionUpdate", ({ editor }) => {
      const textStyleAttrs = editor.getAttributes("textStyle");

      const color = textStyleAttrs?.color;
      if (color != undefined) {
        this.currentFontColor = color;
      } else {
        this.currentFontColor = this.defaultColor;
      }

      const fontSize = textStyleAttrs?.fontSize;
      this.currentFontSize = fontSize || this.currentFontSize;

      this.activeTextModifications.bold = editor.isActive("bold");
      this.activeTextModifications.italic = editor.isActive("italic");
      this.activeTextModifications.underline = editor.isActive("underline");
    });
  }

  private handleFileDrop(view: any, event: DragEvent) {
    if (!event.dataTransfer?.files?.length) return false;

    Array.from(event.dataTransfer.files).forEach((file) => {
      if (file.type.startsWith("image/")) {
        this.insertImageAtMouse(file, event);
      } else {
        this.insertDocumentAtMouse(file, event);
      }
    });

    event.preventDefault();
    return true;
  }

  private insertDocumentAtMouse(file: File, event: DragEvent | null = null) {
    const id = crypto.randomUUID();
    const fileName = file.name;
    const unique = Date.now().toString();

    if (fileName.length > AppConfig.MaxFileNameLength) {
      AppGlobals.ShowErrorMessage($localize`:@@fileNameTooLongError:File name is too long. Maximum allowed length is ${AppConfig.MaxFileNameLength}:maxLength: characters.`);
      return;
    }
    let pos = null;

    if (event) {
      pos = this.editor.view.posAtCoords({
        left: event.clientX,
        top: event.clientY,
      })?.pos;
    }

    if (!this.spark.content.files) {
      this.spark.content.files = {};
    }

    this.spark.content.files[id] = {
      file,
      fileName,
      fileType: file.type,
    };

    this.checkForEmailFilesAndAddTag();

    if (pos === null) {
      this.editor
        .chain()
        .focus()
        .setDocumentAttachment({
          "data-file-id": id,
          "data-file-name": fileName,
        })
        .run();
    } else {
      this.editor
        .chain()
        .focus()
        .setTextSelection(pos)
        .setDocumentAttachment({
          "data-file-id": id,
          "data-file-name": fileName,
        })
        .run();
    }
  }

  private checkForEmailFilesAndAddTag() {
    if (!this.spark || !this.spark.content.files) return;

    const EMAIL_TAG_ID = 1;
    const emailExtensions = ["eml", "msg", "mbox"];

    const hasEmailFiles = Object.values(this.spark.content.files).some(
      (fileInfo) => {
        if (fileInfo.fileName) {
          const extension = fileInfo.fileName.split(".").pop()?.toLowerCase();
          return extension && emailExtensions.includes(extension);
        }
        return false;
      },
    );

    if (hasEmailFiles && !this.spark.tags?.includes(EMAIL_TAG_ID)) {
      if (!this.spark.tags) {
        this.spark.tags = [];
      }
      this.spark.tags.push(EMAIL_TAG_ID);
    }
  }

  handlePlainText(text: string) {
    this.editor.chain().focus().insertContent(text).run();
    return true;
  }

  private insertImageAtMouse(file: File, event: DragEvent) {
    const id = uuidv4();
    if (file.name.length > AppConfig.MaxFileNameLength) {
      AppGlobals.ShowErrorMessage($localize`:@@fileNameTooLongError:File name is too long. Maximum allowed length is ${AppConfig.MaxFileNameLength}:maxLength: characters.`);
      return;
    }
    const blobUrl = URL.createObjectURL(file);
    

    const pos = this.editor.view.posAtCoords({
      left: event.clientX,
      top: event.clientY,
    })?.pos;

    if (pos === undefined) {
      this.editor
        .chain()
        .focus()
        .setResizableImage({
          src: blobUrl,
          "data-file-id": id,
          width: "auto",
          height: "auto",
        })
        .run();
      return;
    }

    this.editor
      .chain()
      .focus()
      .setTextSelection(pos)
      .setResizableImage({
        src: blobUrl,
        "data-file-id": id,
        width: "auto",
        height: "auto",
      })
      .run();

    this.imageFiles.push({ id, file });
    this.spark.content.files = {
      ...this.spark.content.files,
      [id]: { file, fileName: file.name, fileType: file.type, url: blobUrl },
    };
  }

  ngOnDestroy(): void {
    this.userListSubscription?.unsubscribe();
    this.editor?.destroy();
  }

  execute(action: string, value?: any): void {
    const chain = this.editor.chain().focus();
    console.log(action, value);
    switch (action) {
      case "toggle-bold":
        chain.toggleBold().run();
        break;
      case "toggle-italic":
        chain.toggleItalic().run();
        break;
      case "toggle-underline":
        chain.toggleUnderline().run();
        break;
      case "alignLeft":
        chain.setTextAlign("left").run();
        break;
      case "alignCenter":
        chain.setTextAlign("center").run();
        break;
      case "alignRight":
        chain.setTextAlign("right").run();
        break;
      case "orderedList":
        chain.toggleOrderedList().run();
        break;
      case "bulletList":
        chain.toggleBulletList().run();
        break;
      case "taskList":
        chain.toggleTaskList().run();
        break;
      case "addTable":
        chain
          .focus()
          .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
          .run();
        break;
      case "deleteTable":
        chain.deleteTable().run();
        break;
      case "addRow":
        chain.addRowAfter().run();
        break;
      case "deleteRow":
        chain.deleteRow().run();
        break;
      case "addCol":
        chain.addColumnAfter().run();
        break;
      case "deleteCol":
        chain.deleteColumn().run();
        break;
      case "addImage": {
        const url = prompt("Image URL");
        if (url) chain.setImage({ src: url }).run();
        break;
      }
      case "addLink": {
        const url = prompt("Link URL");
        if (url) chain.setLink({ href: url }).run();
        break;
      }
      case "setFontSize": {
        if (value) {
          this.currentFontSize = value;
          chain.setFontSize(value).run();
        }
        break;
      }

      case "setFontColor": {
        if (value) {
          this.currentFontColor = value;
          chain.setColor(value).run();
        } else {
          chain.setColor(this.currentFontColor).run();
        }
      }

      default:
        break;
    }
  }

  async onSaveClick() {
    if (!this.spark) return;

    this.isLoading.set(true);

    try {
      const response = await this.projectHubService.WriteSpark(
        this.boardId,
        this.spark,
      );

      if (response.hasError) {
        AppGlobals.ShowErrorMessage(response.feedback);
      } else {
        this.spark = null;
        this.capturedFiles?.clear();
        this.onSparkSaved.emit();
      }
    } catch (err) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@internalServerError:Internal server error`,
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  onCancelClick() {
    this.spark = null;
    this.imageFiles = [];
    this.onSparkCancelled.emit();
  }
}
